/**
 * Build real PDF files (blob + save) for analytics exports.
 * Layout: print-style header, 4 KPI cards (blue left bar), blue section titles + rules, grey table headers.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtDate, fmtDateFile } from './dateFormat';

const BRAND = [0, 167, 227];
const MARGIN = 40;
const CONTENT_RIGHT = 555;
/** Inset for the main title + metadata (0 = flush left with date row, like reference export). */
const TITLE_BLOCK_INDENT = 0;
/** Extra space between the date/name header row and the large title. */
const HEADER_TO_TITLE_GAP = 10;
/** KPI metric cards — visible corner radius (pt), reference ~6–8px. */
const KPI_CORNER_RADIUS = 7;
const KPI_BAR_WIDTH = 5;
const KPI_CARD_HEIGHT = 60;
const KPI_TEXT_PAD = 9;

/** DD/MM/YYYY, HH:MM — consistent with reference exports. */
function fmtPdfHeaderDate(d) {
  return d.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
}
/** Same line as print preview footer — shown centered at top and bottom of the PDF. */
const BRAND_TAGLINE = 'Webcard Digital • app.webcard.digital';

function ensureSpace(doc, y, need = 100) {
  const h = doc.internal.pageSize.getHeight();
  if (y + need > h - 48) {
    doc.addPage();
    return MARGIN + 8;
  }
  return y;
}

function fmtMetric(v) {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'number' && Number.isNaN(v)) return '—';
  const s = String(v).trim();
  if (s === '' || s.toLowerCase() === 'undefined' || s.toLowerCase() === 'null') return '—';
  return s;
}

function fmtDurationSeconds(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return '0s';
  if (n === 0) return '0s';
  if (n < 0.01) return `${n.toFixed(4)}s`;
  if (n < 1) return `${n.toFixed(2)}s`;
  return `${n % 1 === 0 ? n : n.toFixed(1)}s`;
}
/** Four KPI cards in one row — rounded white panel, blue left bar, grey outline (reference layout). */
function drawKpiCardsFromItems(doc, y, items) {
  const gap = 10;
  const count = 4;
  const totalW = CONTENT_RIGHT - MARGIN;
  const cardW = (totalW - (count - 1) * gap) / count;
  const cardH = KPI_CARD_HEIGHT;
  const r = KPI_CORNER_RADIUS;
  const textXPad = KPI_BAR_WIDTH + KPI_TEXT_PAD;
  const innerW = cardW - textXPad - 6;

  for (let i = 0; i < count; i++) {
    const x = MARGIN + i * (cardW + gap);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, cardW, cardH, r, r, 'F');
    // Clip to the same rounded shape so the blue bar does not square off past the card corners.
    doc.saveGraphicsState();
    doc.roundedRect(x, y, cardW, cardH, r, r, null);
    doc.clip();
    doc.discardPath();
    doc.setFillColor(...BRAND);
    doc.rect(x, y, KPI_BAR_WIDTH, cardH, 'F');
    doc.restoreGraphicsState();
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.65);
    doc.roundedRect(x, y, cardW, cardH, r, r, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    const valStr = items[i].val;
    const lines = doc.splitTextToSize(valStr, innerW);
    doc.text(lines[0], x + textXPad, y + 32);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(items[i].lbl, x + textXPad, y + 50);
  }
  return y + cardH + 28;
}

function drawKpiCards(doc, y, stats) {
  return drawKpiCardsFromItems(doc, y, [
    { val: fmtMetric(stats.total_views ?? 0), lbl: 'TOTAL VIEWS' },
    { val: fmtMetric(stats.unique_visitors ?? 0), lbl: 'UNIQUE VISITORS' },
    { val: fmtMetric(stats.active_days ?? 0), lbl: 'ACTIVE DAYS' },
    { val: fmtDurationSeconds(stats.avg_view_duration), lbl: 'AVG DURATION' },
  ]);
}

function drawSectionTitle(doc, title, y) {
  y = ensureSpace(doc, y, 36);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...BRAND);
  doc.text(title, MARGIN, y);
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(2);
  doc.line(MARGIN, y + 5, CONTENT_RIGHT, y + 5);
  return y + 22;
}

function tableOptions(startY) {
  return {
    startY,
    theme: 'plain',
    styles: {
      fontSize: 9,
      cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
      textColor: [26, 26, 26],
      lineColor: [229, 231, 235],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [249, 250, 251],
      textColor: [100, 100, 100],
      fontStyle: 'bold',
      fontSize: 8,
      lineColor: [229, 231, 235],
      lineWidth: { bottom: 1 },
    },
    bodyStyles: { fontSize: 9, textColor: [26, 26, 26] },
    margin: { left: MARGIN, right: MARGIN },
    tableLineColor: [229, 231, 235],
    tableLineWidth: 0.5,
  };
}

function drawFooter(doc) {
  const pageH = doc.internal.pageSize.getHeight();
  const fy = pageH - 32;
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, fy - 14, CONTENT_RIGHT, fy - 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text(BRAND_TAGLINE, 297.64, fy, { align: 'center' });
}

/**
 * @param {object} analytics — marketing / card analytics payload
 * @param {string} cardUrl — public card slug (e.g. pooja-te0y4)
 * @param {string} [cardDisplayName] — optional name from card profile (e.g. Pooja)
 */
export function downloadMarketingAnalyticsPdf(analytics, cardUrl, cardDisplayName) {
  if (!analytics) return;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  let y = MARGIN;
  const now = new Date();
  const slug = String(cardUrl);
  const titleName = (cardDisplayName && String(cardDisplayName).trim()) || slug;

  const titleX = MARGIN + TITLE_BLOCK_INDENT;
  const titleMaxW = CONTENT_RIGHT - titleX;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(fmtPdfHeaderDate(now), MARGIN, y);
  doc.text(`Analytics - ${slug}`, CONTENT_RIGHT, y, { align: 'right' });
  y += 22 + HEADER_TO_TITLE_GAP;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(0, 0, 0);
  doc.text(`Analytics Report: ${titleName}`, titleX, y, { maxWidth: titleMaxW });
  y += 26;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  doc.text(`Period: ${analytics.period || 'N/A'} • Generated: ${fmtDate(now)}`, titleX, y);
  y += 28;

  y = drawKpiCards(doc, y, analytics.totalStats || {});

  const addTable = (sectionTitle, head, body) => {
    if (!body?.length) return;
    y = drawSectionTitle(doc, sectionTitle, y);
    y = ensureSpace(doc, y, 40 + body.length * 16);
    autoTable(doc, {
      ...tableOptions(y),
      head: [head.map((h) => String(h).toUpperCase())],
      body,
    });
    y = doc.lastAutoTable.finalY + 22;
  };

  if (analytics.viewsOverTime?.length) {
    addTable(
      'Views Over Time',
      ['Date', 'Views', 'Unique Visitors'],
      analytics.viewsOverTime.map((d) => [
        fmtDate(d.date),
        fmtMetric(d.views),
        fmtMetric(d.unique_visitors),
      ]),
    );
  }
  if (analytics.deviceAnalytics?.length) {
    addTable(
      'Device Analytics',
      ['Device', 'Browser', 'OS', 'Views'],
      analytics.deviceAnalytics.map((d) => [
        fmtMetric(d.device_type),
        fmtMetric(d.browser),
        fmtMetric(d.os),
        fmtMetric(d.total_views),
      ]),
    );
  }
  if (analytics.geographicAnalytics?.length) {
    addTable(
      'Geographic Analytics',
      ['Country', 'City', 'Views', 'Unique Visitors'],
      analytics.geographicAnalytics.map((g) => [
        fmtMetric(g.country),
        fmtMetric(g.city),
        fmtMetric(g.total_views),
        fmtMetric(g.unique_visitors),
      ]),
    );
  }
  if (analytics.trafficSources?.length) {
    addTable(
      'Traffic Sources',
      ['Source', 'Medium', 'Views'],
      analytics.trafficSources.map((t) => [
        fmtMetric(t.source),
        fmtMetric(t.medium),
        fmtMetric(t.total_views),
      ]),
    );
  }
  if (analytics.timeAnalytics?.hourly?.length) {
    addTable(
      'Hourly Breakdown',
      ['Hour', 'Views'],
      analytics.timeAnalytics.hourly.map((h) => [fmtMetric(h.hour), fmtMetric(h.views)]),
    );
  }

  drawFooter(doc);
  doc.save(`analytics-${String(cardUrl).replace(/[^\w.-]+/g, '_')}-${fmtDateFile(new Date())}.pdf`);
}

/**
 * @param {object} analytics — QR scan analytics (paid payload)
 * @param {object} qrCode
 */
export function downloadQrAnalyticsPdf(analytics, qrCode) {
  if (!analytics) return;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  let y = MARGIN;
  const label = qrCode?.label || qrCode?.uid || 'QR';
  const now = new Date();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(fmtPdfHeaderDate(now), MARGIN, y);
  doc.text(`QR Analytics - ${label}`, CONTENT_RIGHT, y, { align: 'right' });
  y += 22 + HEADER_TO_TITLE_GAP;

  const titleX = MARGIN + TITLE_BLOCK_INDENT;
  const titleMaxW = CONTENT_RIGHT - titleX;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(0, 0, 0);
  doc.text('QR Analytics Report', titleX, y);
  y += 24;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  doc.text(`Generated: ${fmtDate(now)}`, titleX, y);
  y += 16;
  const dest = qrCode?.target_url ? doc.splitTextToSize(String(qrCode.target_url), titleMaxW) : ['—'];
  doc.text(dest, titleX, y);
  y += dest.length * 12 + 18;

  y = drawKpiCardsFromItems(doc, y, [
    { val: fmtMetric(analytics.totalScans ?? 0), lbl: 'TOTAL SCANS' },
    { val: fmtMetric((analytics.scansByCountry || []).length), lbl: 'COUNTRIES' },
    { val: fmtMetric((analytics.scansByDevice || []).length), lbl: 'DEVICE TYPES' },
    { val: fmtMetric((analytics.scansByBrowser || []).length), lbl: 'BROWSER TYPES' },
  ]);

  const addTable = (sectionTitle, head, body) => {
    if (!body?.length) return;
    y = drawSectionTitle(doc, sectionTitle, y);
    y = ensureSpace(doc, y, 40 + body.length * 16);
    autoTable(doc, {
      ...tableOptions(y),
      head: [head.map((h) => String(h).toUpperCase())],
      body,
    });
    y = doc.lastAutoTable.finalY + 22;
  };

  addTable(
    'Scans by Day',
    ['Date', 'Scans'],
    (analytics.scansByDay || []).map((r) => [fmtDate(r.date), fmtMetric(r.scans)]),
  );
  addTable(
    'Top Countries',
    ['Country', 'Scans'],
    (analytics.scansByCountry || []).map((r) => [fmtMetric(r.country || 'Unknown'), fmtMetric(r.scans)]),
  );
  addTable(
    'Devices',
    ['Type', 'Scans'],
    (analytics.scansByDevice || []).map((r) => [fmtMetric(r.device_type || 'Unknown'), fmtMetric(r.scans)]),
  );
  addTable(
    'Browsers',
    ['Browser', 'Scans'],
    (analytics.scansByBrowser || []).map((r) => [fmtMetric(r.browser || 'Unknown'), fmtMetric(r.scans)]),
  );

  drawFooter(doc);
  doc.save(`qr-analytics-${String(qrCode?.uid || 'export').replace(/[^\w.-]+/g, '_')}-${fmtDateFile(new Date())}.pdf`);
}
